import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Button,
    Checkbox,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableRow,
    Paper,
    TextField,
    CircularProgress
} from '@mui/material';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import api from '../../services/CBT';
import { Link } from "react-router-dom";
import { CiClock2 } from 'react-icons/ci';
import Title from '../Dashboard/Title';


const ViewTest = () => {
    const [data, setData] = useState([]);
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [fetchingData, setFetchingData] = useState(true);
    const [viewedQuestions, setViewedQuestions] = useState(new Set([0]));
    const { admToken, user } = useSelector((state) => state.auth);
    const { subject: encodedSubject, subjectId, title: encodedTitle, testId, duration } = useParams();

    const subject = decodeURIComponent(encodedSubject || '');
    const title = decodeURIComponent(encodedTitle || '');

    const getCBTQuestions = useCallback(async () => {
        try {
            setFetchingData(true);

            const res = await api.getCBTQuestions(admToken, user.schoolId, subjectId, testId);
            if (res.status && res.statusCode === 2) {
                setData(res.data);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setFetchingData(false);
        }
    }, [admToken, user.schoolId, subjectId]);

    useEffect(() => {
        getCBTQuestions();
    }, [getCBTQuestions]);

    const handleNextQuestion = () => {
        const nextIndex = currentQuestionIndex + 1;
        if (nextIndex < data.length) {
            setCurrentQuestionIndex(nextIndex);
            setViewedQuestions(prev => new Set([...prev, nextIndex]));
        }
    };

    const handleQuestionNavigation = (index) => {
        setCurrentQuestionIndex(index);
        setViewedQuestions(prev => new Set([...prev, index]));
    };

    const currentQuestion = data[currentQuestionIndex] || {};
    const totalQuestions = data.length;
    const options = currentQuestion.options || {};

    if (fetchingData) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <CircularProgress size={60} sx={{ color: "#671E78" }} />
            </Box>
        );
    }

    return (
        <Box>
            <Title title="CBT - View Test" />
            <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                padding: "20px",
                gap: "20px"
            }}>
                <Box sx={{ flex: 2 }}>
                    <h1 className="font-bold text-2xl mb-4">CBT</h1>
                    <div className='p-5 flex justify-between rounded-lg bg-gray-200 shadow-sm mb-8'>
                        <div>
                            <h1 className="font-bold text-lg">{subject}</h1>
                            <div className="flex items-center gap-3 pt-12 text-sm">
                                <span><CiClock2 /></span>
                                <span>{`${duration} mins` || "0 min"}</span>
                            </div>
                        </div>
                        <div>
                            <p className="font-bold text-lg">Total Questions: {totalQuestions}</p>
                            <p className="px-6 py-1 text-sm mt-12 bg-white rounded-full">{title}</p>
                        </div>
                    </div>
                    {currentQuestion && (
                        <>
                            <Box sx={{ mb: 4 }}>
                                <Typography variant="h6" sx={{ fontFamily: "Gilroy-Medium", mb: 2 }}>
                                    {currentQuestionIndex + 1}. {currentQuestion.question}
                                </Typography>

                                {currentQuestion.attachments && currentQuestion.attachments.length > 0 && (
                                    <Box sx={{ mb: 2 }}>
                                        {currentQuestion.attachments.map((file, index) => {
                                            const fileUrl = file?.filePath
                                            console.log(fileUrl);
                                            return (
                                                <Box key={index} sx={{ mt: 1 }}>
                                                    <img
                                                        src={fileUrl}
                                                        alt={`attachment-${index}`}
                                                        style={{
                                                            height: "250px",
                                                            width: "100%",
                                                            borderRadius: "8px",
                                                            marginTop: "8px",
                                                        }}
                                                    />
                                                </Box>
                                            );
                                        })}
                                    </Box>
                                )}

                                {currentQuestion.answerType === "F" ? (
                                    <TextField
                                        fullWidth
                                        variant="outlined"
                                        placeholder="Enter your answer here"
                                        sx={{
                                            "& .MuiOutlinedInput-root": {
                                                "& fieldset": {
                                                    borderColor: "#671E78",
                                                },
                                            },
                                        }}
                                    />
                                ) : (
                                    <>
                                        <Typography variant="subtitle2" sx={{ fontFamily: "Gilroy-Regular", mb: 2, color: "#666" }}>
                                            {currentQuestion.answerType === "M" ? "[Select multiple answers]" : currentQuestion.answerType === "S" ? "[Select one answer]" : "[Fill in the gap]"}
                                        </Typography>
                                        <Box sx={{ ml: 2 }}>
                                            {['A', 'B', 'C', 'D', 'E'].map((option) => (
                                                options[`option${option}`] && (
                                                    <Box key={option} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                                        <Checkbox
                                                            sx={{
                                                                color: '#671E78',
                                                                '&.Mui-checked': {
                                                                    color: '#671E78',
                                                                },
                                                            }}
                                                        />
                                                        <Typography sx={{ fontFamily: "Gilroy-Regular" }}>
                                                            {options[`option${option}`]}
                                                        </Typography>
                                                    </Box>
                                                )
                                            ))}
                                        </Box>
                                    </>
                                )}
                            </Box>
                        </>
                    )}
                </Box>

                <Box sx={{
                    flex: 1,
                    backgroundColor: "rgba(103, 30, 120, 0.1)",
                    padding: "20px",
                    borderRadius: "10px",
                    marginTop: "46px"
                }}>

                    <TableContainer component={Paper} sx={{
                        mb: 3,
                        boxShadow: 'none',
                        border: '1px solid #e0e0e0',
                        backgroundColor: "#ffffff"
                    }}>
                        <Table>
                            <TableBody>
                                {Array.from({ length: Math.ceil(totalQuestions / 5) }).map((_, row) => (
                                    <TableRow key={row} sx={{ border: '1px solid #e0e0e0', backgroundColor: "#ffffff" }}>
                                        {[0, 1, 2, 3, 4].map((col) => {
                                            const questionNumber = row * 5 + col;
                                            return questionNumber < totalQuestions ? (
                                                <TableCell
                                                    key={col}
                                                    align="center"
                                                    sx={{
                                                        border: '1px solid #e0e0e0',
                                                        backgroundColor: "#ffffff",
                                                        padding: "8px"
                                                    }}
                                                >
                                                    <Button
                                                        variant={viewedQuestions.has(questionNumber) ? "contained" : "outlined"}
                                                        sx={{
                                                            minWidth: 0,
                                                            minHeight: 0,
                                                            width: 30,
                                                            height: 30,
                                                            backgroundColor: viewedQuestions.has(questionNumber) ? '#671E78' : 'transparent',
                                                            color: viewedQuestions.has(questionNumber) ? '#fff' : '#000',
                                                            '&:hover': {
                                                                backgroundColor: viewedQuestions.has(questionNumber) ? '#4e1661' : 'rgba(103, 30, 120, 0.1)'
                                                            }
                                                        }}
                                                        onClick={() => handleQuestionNavigation(questionNumber)}
                                                    >
                                                        {questionNumber + 1}
                                                    </Button>
                                                </TableCell>
                                            ) : (
                                                <TableCell key={col} sx={{ border: '1px solid #e0e0e0', backgroundColor: "#ffffff" }} />
                                            );
                                        })}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <Box sx={{ mb: 3 }}>
                        <Typography variant="body2" sx={{ fontFamily: "Gilroy-Regular", textAlign: 'center' }}>
                            Question
                        </Typography>
                        <Typography variant="body2" sx={{
                            fontFamily: "Gilroy-Medium",
                            textAlign: 'center',
                            fontSize: "22px",
                            color: "#671E78"
                        }}>
                            {currentQuestionIndex + 1} of {totalQuestions}
                        </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Link to="/cbt">
                            <Button
                                variant="outlined"
                                sx={{
                                    textTransform: 'initial',
                                    fontFamily: 'Gilroy-Regular',
                                    color: '#671E78',
                                    borderColor: '#671E78',
                                    '&:hover': {
                                        backgroundColor: 'rgba(103, 30, 120, 0.1)',
                                        borderColor: '#671E78'
                                    }
                                }}
                            >
                                Exit Preview
                            </Button>
                        </Link>

                        <Button
                            variant="contained"
                            sx={{
                                textTransform: 'initial',
                                fontFamily: 'Gilroy-Regular',
                                backgroundColor: '#671E78',
                                '&:hover': {
                                    backgroundColor: '#4e1661'
                                }
                            }}
                            onClick={handleNextQuestion}
                            disabled={currentQuestionIndex === totalQuestions - 1}
                        >
                            Next →
                        </Button>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default ViewTest;